import type { ReactNode } from 'react';
import { Alert, Box, Card, CardContent, Chip, Skeleton, Stack, Typography } from '@mui/material';
import EmojiEventsRounded from '@mui/icons-material/EmojiEventsRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { Link as RouterLink } from 'react-router';
import { fmt, p2Necessaria } from './calculo';
import type { Insight } from './insights';
import { nomeProprio } from './texto';

/** Número em destaque: quando a história é um número, o número é o gráfico. */
export function Destaque({
  rotulo,
  valor,
  rodape,
  icone,
}: {
  rotulo: string;
  valor: ReactNode;
  rodape?: ReactNode;
  icone?: ReactNode;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
          {icone}
          <Typography variant="body2">{rotulo}</Typography>
        </Stack>
        <Typography
          variant="h3"
          component="p"
          sx={{ mt: 0.5, mb: 0.5, fontVariantNumeric: 'tabular-nums' }}
        >
          {valor}
        </Typography>
        {rodape && (
          <Typography variant="body2" color="text.secondary">
            {rodape}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

const ICONE_TOM = {
  bom: <EmojiEventsRounded fontSize="small" />,
  neutro: <TrendingUpRounded fontSize="small" />,
  atencao: <WarningAmberRounded fontSize="small" />,
};

export function CartaoInsight({ insight }: { insight: Insight }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.75 }}>
          <Box
            sx={(t) => ({
              display: 'grid',
              placeItems: 'center',
              width: 30,
              height: 30,
              borderRadius: '10px',
              color:
                insight.tom === 'atencao'
                  ? t.vars.palette.warning.main
                  : t.vars.palette.primary.main,
              bgcolor:
                insight.tom === 'atencao'
                  ? t.alpha(t.vars.palette.warning.main, 0.12)
                  : t.vars.palette.primary.container,
            })}
          >
            {ICONE_TOM[insight.tom]}
          </Box>
          <Typography variant="subtitle2">{insight.titulo}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {insight.texto}
        </Typography>
      </CardContent>
    </Card>
  );
}

/** Situação da P1 em relação a 6, com ícone + texto (a cor nunca é a única pista). */
export function ChipSituacaoP1({ p1 }: { p1: number | null | undefined }) {
  if (typeof p1 !== 'number') return <Chip size="small" label="P1 pendente" />;
  const falta = p2Necessaria(p1);
  if (p1 >= 6)
    return (
      <Chip
        size="small"
        color="success"
        variant="outlined"
        label={`Precisa de ${fmt(falta)} na P2`}
      />
    );
  if (falta <= 10)
    return (
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={`Precisa de ${fmt(falta)} na P2`}
      />
    );
  return <Chip size="small" color="error" variant="outlined" label="Rumo à P3" />;
}

export function AvisoVendoComo({ nome }: { nome: string }) {
  return (
    <Alert
      severity="info"
      icon={<InfoOutlined />}
      sx={{ mb: 3 }}
      action={<RouterLink to="/admin">Voltar</RouterLink>}
    >
      Você está vendo o painel exatamente como <strong>{nomeProprio(nome)}</strong> vê.
    </Alert>
  );
}

export function CarregandoPainel() {
  return (
    <Stack spacing={2}>
      <Skeleton variant="text" width={280} height={48} />
      <Stack direction="row" spacing={2}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={128} sx={{ flex: 1, borderRadius: 4 }} />
        ))}
      </Stack>
      <Skeleton variant="rounded" height={260} sx={{ borderRadius: 4 }} />
    </Stack>
  );
}
